import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserControlsContext } from '../../contexts/UserControlsContext';
import { hasRequiredRole } from '../../lib/roles';
import {
  WarningsTab,
  useDualWriteStream,
  initWarningRules,
  saveWarningRules,
  applyWarningRules,
} from './dual-write';
import type { WarningRule } from './dual-write';

const AdminHttpLogs = React.lazy(() => import('../../AdminHttpLogs'));
const DualWriteDashboard = React.lazy(() =>
  import('./dual-write/DashboardTab').then((m) => ({ default: m.DashboardTab })),
);
const SlotResolutionTab = React.lazy(() =>
  import('./diagnostics/SlotResolutionTab').then((m) => ({ default: m.SlotResolutionTab })),
);

type Tab = 'logs' | 'dashboard' | 'warnings' | 'diagnostics';

const VALID_TABS: Set<string> = new Set<string>(['logs', 'dashboard', 'warnings', 'diagnostics']);

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'logs', label: 'Logs' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'warnings', label: 'Warnings' },
  { key: 'diagnostics', label: 'Diagnostics' },
];

/**
 * Backend bucket each tab is actually gated on (U8 retry — Codex-found gap):
 * Logs reads GET /api/admin/http-logs (usage_viewer); Dashboard/Warnings/
 * Diagnostics all read /api/v4/admin/dual-write/* or
 * /api/v4/admin/blueprints/diagnostics/* (config_editor). The route-level
 * guard in AppRouter accepts either role so both bucket owners can reach
 * this page — this table is what keeps a role from being routed onto a tab
 * whose data call would 403.
 */
const TAB_ROLES: Record<Tab, string[]> = {
  logs: ['usage_viewer'],
  dashboard: ['config_editor'],
  warnings: ['config_editor'],
  diagnostics: ['config_editor'],
};

/** Pure seam (unit-tested): which tabs the caller's roles can actually load. */
export function getAccessibleTabs(roles: string[] | undefined): Tab[] {
  return TABS.filter((t) => hasRequiredRole(roles, TAB_ROLES[t.key])).map((t) => t.key);
}

/**
 * Pure seam (unit-tested): resolve the tab to render. Honors a valid,
 * accessible URL tab; otherwise falls back to the first tab the caller's
 * roles unlock (never a hardcoded 'logs' — that would 403 a config_editor
 * -only caller with no usage_viewer grant). Returns null only if the caller
 * has access to no tab at all (shouldn't happen given the route guard, but
 * defensive rather than throwing).
 */
export function pickDefaultTab(requested: string | undefined, accessible: Tab[]): Tab | null {
  if (requested && VALID_TABS.has(requested) && accessible.includes(requested as Tab)) {
    return requested as Tab;
  }
  return accessible[0] ?? null;
}

export default function IngestionPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { roles } = useUserControlsContext();
  const accessibleTabs = useMemo(() => getAccessibleTabs(roles), [roles]);
  const visibleTabs = useMemo(
    () => TABS.filter((t) => accessibleTabs.includes(t.key)),
    [accessibleTabs],
  );
  const activeTab = pickDefaultTab(tab, accessibleTabs);
  const setActiveTab = (t: Tab) => navigate(`/admin/ingestion/${t}`, { replace: true });

  // SSE stream for bridge tabs
  const { events, connected } = useDualWriteStream();

  // Warning rules — shared between Dashboard and Warnings tabs
  const [warningRules, setWarningRules] = useState<WarningRule[]>(() => initWarningRules());

  const toggleWarningRule = useCallback((ruleId: string) => {
    setWarningRules((prev) => {
      const next = prev.map((r) => r.id === ruleId ? { ...r, enabled: !r.enabled } : r);
      saveWarningRules(next);
      return next;
    });
  }, []);

  const warningEvents = events.filter(e => e.type === 'warning');
  const activeWarningEvents = applyWarningRules(warningEvents, warningRules);
  const suppressedWarningCount = warningEvents.length - activeWarningEvents.length;

  if (!activeTab) {
    return (
      <div className="flex items-center justify-center h-64 text-icon-subtle text-sm">
        None of your roles unlock an Ingestion tab.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 border-b border-border-secondary mb-6">
        {visibleTabs.map((tabEntry) => (
          <button
            key={tabEntry.key}
            onClick={() => setActiveTab(tabEntry.key)}
            className={`px-3 py-3 text-sm border-b-2 flex items-center gap-1.5 ${
              activeTab === tabEntry.key
                ? 'border-blueberry-500 text-content-primary font-medium'
                : 'border-transparent text-icon-subtle hover:text-content-tertiary'
            }`}
          >
            {tabEntry.label}
            {tabEntry.key === 'warnings' && activeWarningEvents.length > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-tangerine-50 text-tangerine-600 text-xs font-medium min-w-[1.25rem] h-5 px-1">
                {activeWarningEvents.length}
              </span>
            )}
            {tabEntry.key === 'warnings' && suppressedWarningCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-bg-tertiary text-icon-subtle text-[10px] font-medium min-w-[1.25rem] h-5 px-1">
                +{suppressedWarningCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-icon-subtle">Loading...</div>}>
        {activeTab === 'logs' && <AdminHttpLogs />}
        {activeTab === 'dashboard' && (
          <DualWriteDashboard
            events={events}
            connected={connected}
            warningStats={{ active: activeWarningEvents.length, suppressed: suppressedWarningCount }}
          />
        )}
        {activeTab === 'warnings' && (
          <WarningsTab events={warningEvents} rules={warningRules} onToggleRule={toggleWarningRule} />
        )}
        {activeTab === 'diagnostics' && <SlotResolutionTab />}
      </React.Suspense>
    </div>
  );
}
